import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { DEFAULT_NO_FORUM, courtColor } from "@/lib/forumColors";
import { useAuth } from "@/context/AuthContext";

const NO_FORUM = "no_forum";

const fallback = {
  courts: [],
  colorMap: {},
  noForumColor: DEFAULT_NO_FORUM,
  hidden: [],
  colorFor: () => DEFAULT_NO_FORUM,
  forumName: () => "No forum",
  forumKeyOf: () => NO_FORUM,
  isVisible: () => true,
  visibleEvents: (events) => events || [],
  slicesFor: () => [],
  toggleForum: () => {},
  showAllForums: () => {},
  refresh: () => {},
};

const ForumColorContext = createContext(fallback);

export const useForumColors = () => useContext(ForumColorContext) || fallback;

export const ForumColorProvider = ({ children }) => {
  const { user, role, workspace } = useAuth();
  const [courts, setCourts] = useState([]);
  const [hidden, setHidden] = useState([]);
  const noForumColor = user?.preferences?.no_forum_color || DEFAULT_NO_FORUM;
  const storeKey = `court-diary-hidden-forums:${workspace?.workspace_id || "self"}`;

  const refresh = useCallback(async () => {
    if (!user || role === "clerk") return;
    try {
      const { data } = await api.get("/courts");
      setCourts(data);
    } catch {
      setCourts([]);
    }
  }, [user, role, workspace?.workspace_id]);

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener("diary-refresh", handler);
    window.addEventListener("forum-colors-changed", handler);
    return () => {
      window.removeEventListener("diary-refresh", handler);
      window.removeEventListener("forum-colors-changed", handler);
    };
  }, [refresh]);

  useEffect(() => {
    try {
      setHidden(JSON.parse(window.localStorage.getItem(storeKey) || "[]"));
    } catch {
      setHidden([]);
    }
  }, [storeKey]);

  const persist = useCallback(
    (update) => {
      setHidden((prev) => {
        const next = typeof update === "function" ? update(prev) : update;
        window.localStorage.setItem(storeKey, JSON.stringify(next));
        return next;
      });
    },
    [storeKey],
  );

  const value = useMemo(() => {
    const colorMap = {};
    const nameMap = {};
    courts.forEach((court, i) => {
      colorMap[court.court_id] = courtColor(court, i);
      nameMap[court.court_id] = court.name;
    });

    const forumKeyOf = (event) => event?.court_id || NO_FORUM;
    const colorFor = (event) =>
      colorMap[event?.court_id] || event?.court_color || event?.color || noForumColor;
    const forumName = (event) => nameMap[event?.court_id] || event?.court_name || "No forum";
    const isVisible = (event) => !hidden.includes(forumKeyOf(event));

    const slicesFor = (events) => {
      const map = {};
      (events || []).forEach((event) => {
        const key = forumKeyOf(event);
        const slice = map[key] || {
          key,
          name: forumName(event),
          color: colorFor(event),
          count: 0,
        };
        slice.count += 1;
        map[key] = slice;
      });
      return Object.values(map).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    };

    return {
      courts,
      colorMap,
      noForumColor,
      hidden,
      colorFor,
      forumName,
      forumKeyOf,
      isVisible,
      visibleEvents: (events) => (events || []).filter(isVisible),
      slicesFor,
      toggleForum: (key) =>
        persist((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key])),
      showAllForums: () => persist([]),
      refresh,
    };
  }, [courts, noForumColor, hidden, persist, refresh]);

  return <ForumColorContext.Provider value={value}>{children}</ForumColorContext.Provider>;
};
